import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  workspacesApi,
  ApiError,
  type Workspace,
  type WorkspaceDataSummaryResponse,
} from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/components/AuthProvider';
import {
  applyWorkspaceUpdateToList,
  removeWorkspaceFromList,
  sumMovedCounts,
  isMoveRequiredError,
} from '@/lib/workspaces/workspaceDelete';

export type { Workspace } from '@/lib/api';

const SELECTED_WORKSPACE_KEY = 'selectedWorkspaceId';
export const WORKSPACES_ERROR_RETRY_MS = 30_000;

export function useWorkspaces() {
  const { user } = useAuth();

  return useQuery<Workspace[]>({
    queryKey: ['workspaces-v1', user?.id || 'no-user'],
    queryFn: async (): Promise<Workspace[]> => {
      const result = await workspacesApi.getAll();
      if (result.error) throw ApiError.fromResult(result);
      return Array.isArray(result.data) ? result.data : [];
    },
    enabled: !!user,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retryOnMount: false,
    refetchInterval: (query) => {
      if (query.state.status !== 'error') return false;
      const status = (query.state.error as { statusCode?: unknown } | null)?.statusCode;
      return typeof status === 'number' && status >= 400 && status < 500 ? false : WORKSPACES_ERROR_RETRY_MS;
    },
  });
}

export function useCreateWorkspace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name: string }) => {
      const result = await workspacesApi.create(data);
      if (result.error) throw ApiError.fromResult(result);
      return result.data as Workspace;
    },
    onSuccess: (newWorkspace) => {
      queryClient.setQueriesData<Workspace[]>({ queryKey: ['workspaces-v1'] }, (old) => {
        const list = Array.isArray(old) ? old : [];
        return list.some((w) => w.id === newWorkspace.id) ? list : [...list, newWorkspace];
      });
      queryClient.invalidateQueries({ queryKey: ['workspaces-v1'] });
      toast({ variant: 'success', title: 'Workspace created' });
    },
    onError: (error: any) => {
      toast({ variant: 'destructive', title: 'Could not create workspace', description: error?.message });
    },
  });
}

export function useUpdateWorkspace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { name?: string; is_default?: boolean; image?: string | null } }) => {
      const result = await workspacesApi.update(id, data);
      if (result.error) throw ApiError.fromResult(result);
      return result.data as Workspace;
    },
    onSuccess: (updated) => {
      queryClient.setQueriesData<Workspace[]>({ queryKey: ['workspaces-v1'] }, (old) =>
        Array.isArray(old) ? applyWorkspaceUpdateToList(old, updated) : old,
      );
      queryClient.invalidateQueries({ queryKey: ['workspaces-v1'] });
      toast({ variant: 'success', title: 'Workspace updated' });
    },
    onError: (error: any) => {
      toast({ variant: 'destructive', title: 'Could not update workspace', description: error?.message });
    },
  });
}

export function useWorkspaceDataSummary(id: string | null | undefined) {
  return useQuery<WorkspaceDataSummaryResponse>({
    queryKey: ['workspace-data-summary', id],
    queryFn: async (): Promise<WorkspaceDataSummaryResponse> => {
      const result = await workspacesApi.getDataSummary(id as string);
      if (result.error) throw ApiError.fromResult(result);
      return result.data as WorkspaceDataSummaryResponse;
    },
    enabled: !!id,
    staleTime: 0, // always fresh — this drives a delete decision
    gcTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useDeleteWorkspace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, moveToWorkspaceId }: { id: string; moveToWorkspaceId?: string }) => {
      const result = await workspacesApi.delete(id, moveToWorkspaceId ? { move_to_workspace_id: moveToWorkspaceId } : undefined);
      if (result.error) throw ApiError.fromResult(result);
      return result.data as { id: string; deleted: true; moved: Record<string, number> };
    },
    onSuccess: (deleted, variables) => {
      queryClient.setQueriesData<Workspace[]>({ queryKey: ['workspaces-v1'] }, (old) =>
        Array.isArray(old) ? removeWorkspaceFromList(old, variables.id) : old,
      );
      queryClient.invalidateQueries({ queryKey: ['workspaces-v1'] });
      queryClient.removeQueries({ queryKey: ['workspace-data-summary', variables.id] });
      const movedCount = sumMovedCounts(deleted.moved);
      toast({
        variant: 'success',
        title: 'Workspace deleted',
        description: movedCount > 0 ? `${movedCount} record${movedCount === 1 ? '' : 's'} moved to the other workspace.` : undefined,
      });
    },
    onError: (error: any) => {
      if (isMoveRequiredError(error)) return;
      toast({ variant: 'destructive', title: 'Could not delete workspace', description: error?.message });
    },
  });
}

export function getSelectedWorkspaceId(): string | null {
  try {
    const v = localStorage.getItem(SELECTED_WORKSPACE_KEY);
    return v && v.trim() ? v : null;
  } catch {
    return null;
  }
}

export function setSelectedWorkspaceId(id: string | null) {
  try {
    if (id) localStorage.setItem(SELECTED_WORKSPACE_KEY, id);
    else localStorage.removeItem(SELECTED_WORKSPACE_KEY);
  } catch {
  }
}

export function resolveCurrentWorkspace(
  workspaces: Workspace[] | undefined,
  persistedId: string | null,
): Workspace | null {
  if (!workspaces || workspaces.length === 0) return null;
  if (persistedId) {
    const match = workspaces.find((w) => w.id === persistedId);
    if (match) return match;
  }
  return workspaces.find((w) => w.is_default) || workspaces[0];
}
